// Pure configuration for the AI Usage → Trends view. No React, no I/O, so the
// breakdown/metric/bucket rules are unit-testable and shared by the control
// bar, the chart and the tables.

// BREAKDOWNS are ordered by financial usefulness rather than technical detail.
// A CFO cares far more that "Customer Support AI increased 46%" than that
// "sk-prod-42 increased 46%", so the ownership perspectives lead and API key
// comes last. `ownership` marks the four allocation perspectives, whose values
// are ALLOCATED by the same rule that assigns cost, not metered — the UI must
// label them so nobody reads them as measured consumption.
//
// ids must exactly match the backend's accepted breakdown set (rejected
// otherwise, not silently defaulted) — see breakdownColumns in
// ../../backend_go/cmd/api/ai_trends_query.go.
export const BREAKDOWNS = [
  { id: "use_case", label: "Use case", ownership: true },
  { id: "organisation", label: "Organisation", ownership: true },
  { id: "technical", label: "Technical", ownership: true },
  { id: "financial", label: "Financial", ownership: true },
  { id: "provider", label: "Provider", ownership: false },
  { id: "model", label: "Model", ownership: false },
  { id: "api_key", label: "API key", ownership: false },
];

export function breakdownById(id) {
  return BREAKDOWNS.find((b) => b.id === id) || BREAKDOWNS[0];
}

// METRICS switch what the same historical analysis is measured in. Changing
// metric preserves period, comparison, breakdown and filters, which is what
// makes "24% of tokens but 51% of economic value" a single click.
// Input/output/cached token splits are deliberately absent: they are
// diagnostic, not financial, and would turn this into an observability page.
//
// ids are a wire contract with the endpoint, not display strings: the chart
// indexes each series point by metric id, and these are the exact JSON keys
// GET /api/v1/ai/trends emits. Do not rename them for readability.
export const METRICS = [
  { id: "tokens", label: "Tokens", kind: "tokens" },
  { id: "events", label: "Events", kind: "count" },
  { id: "actual", label: "Actual cost", kind: "money" },
  { id: "apiEquivalent", label: "API-equivalent cost", kind: "money" },
];

export function metricById(id) {
  return METRICS.find((m) => m.id === id) || METRICS[0];
}

// bucketFor mirrors the backend's rule (cmd/api/ai_trends_query.go bucketFor)
// so the axis label the user reads always matches the grain the server
// returned. Kept in sync deliberately: the server is authoritative, this is
// for labelling only.
//
// There is deliberately no hourly bucket: the AI cost base
// (allocation_cost_base_daily) is DAY-grain, so anything finer would be a
// fabricated label on a daily bar. Intra-day analysis belongs to the Live
// mode, which reads a separate per-event source. Boundary: 90 days is the
// last daily window.
export function bucketFor(fromISO, toISO) {
  const from = new Date(`${fromISO}T00:00:00Z`);
  const to = new Date(`${toISO}T00:00:00Z`);
  const days = Math.round((to - from) / 86400000) + 1;
  if (days <= 90) return "day";
  return "week";
}

export const BUCKET_LABELS = { day: "Daily", week: "Weekly" };
